/* ============================================================================
 * NEWS_DESK › Lexicon — the words a community owns
 * ----------------------------------------------------------------------------
 * The other half of word publishing. A post is read once and ages; a term is
 * looked up, so it needs a status, a steward, and a supersession trail rather
 * than an edit history.
 *
 * The four entries here are the system's own first arbitrations — the design
 * language really was carrying four names at once with no source of truth, and
 * this is the instrument that settles it. Not sample data.
 * ========================================================================= */

import {
  LEXICON_STORE,
  SEED_TERMS,
  TERM_STATUS_LED,
  retiredTerms,
  type Term,
} from '../lib/lexicon';

function StatusMark({ term }: { term: Term }) {
  return (
    <span className="row" style={{ gap: 6 }}>
      <img
        className="led"
        src={TERM_STATUS_LED[term.status]}
        width={8}
        height={8}
        alt=""
        aria-hidden="true"
      />
      <span className="micro micro--ink">{term.status}</span>
    </span>
  );
}

function TermCard({ term }: { term: Term }) {
  return (
    <div className="sheet">
      <div className="page__bar">
        <div className="grow">
          <p className="section-h">
            {term.headword}
            <span className="dim">{term.namespace}</span>
          </p>
          <p className="cap">{term.definition}</p>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <StatusMark term={term} />
          {term.enforced ? (
            <span className="badge badge-live">enforced</span>
          ) : (
            <span className="badge badge-claude">editorial</span>
          )}
        </div>
      </div>

      <dl className="kv" style={{ marginTop: 'var(--s-4)' }}>
        <dt>steward</dt>
        <dd>{term.steward}</dd>
        <dt>entered canon</dt>
        <dd className="mono">{term.since}</dd>
        {term.aliases.length > 0 && (
          <>
            <dt>also resolves</dt>
            <dd className="mono">{term.aliases.join(' · ')}</dd>
          </>
        )}
        {term.seeAlso.length > 0 && (
          <>
            <dt>see also</dt>
            <dd className="mono">{term.seeAlso.join(' · ')}</dd>
          </>
        )}
        {term.replaces && (
          <>
            <dt>replaces</dt>
            <dd className="mono">{term.replaces}</dd>
          </>
        )}
        {term.supersededBy && (
          <>
            <dt>superseded by</dt>
            <dd>
              <span className="badge badge-claude">
                do not use — say “{term.supersededBy}”
              </span>
            </dd>
          </>
        )}
        <dt>provenance</dt>
        <dd className="cap">{term.provenance}</dd>
      </dl>

      {term.note && (
        <p className="cap" style={{ marginTop: 'var(--s-3)' }}>
          {term.note}
        </p>
      )}
    </div>
  );
}

export function NewsDeskLexicon() {
  const retired = retiredTerms();

  return (
    <div className="dsp stack">
      <div className="page__bar">
        <div className="grow">
          <p className="section-h">
            lexicon <span className="dim">the words the community owns</span>
          </p>
          <p className="cap">
            A term is not a post. It is looked up rather than read in sequence, so
            it carries a status, a steward and a supersession trail — a canonical
            definition is never silently rewritten.
          </p>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <span className="badge badge-admin">{SEED_TERMS.length} terms</span>
          <span className="badge badge-claude">store not wired</span>
        </div>
      </div>

      <div className="notice notice--brass">
        <span className="tick" aria-hidden="true" />
        <span>
          <strong>Held as data, not stored.</strong> {LEXICON_STORE.blockedBy}.
          Planned: {LEXICON_STORE.planned}.
        </span>
      </div>

      {/* ── the retired list, first: the point of a lexicon is that you can tell
             what not to say ───────────────────────────────────────────────── */}
      {retired.length > 0 && (
        <div className="sheet">
          <p className="section-h">
            retired <span className="dim">do not use</span>
          </p>
          <div className="sheet-rows">
            {retired.map((t) => (
              <div className="lrow" key={t.headword}>
                <img
                  className="led"
                  src={TERM_STATUS_LED[t.status]}
                  width={8}
                  height={8}
                  alt=""
                  aria-hidden="true"
                />
                <span className="lrow__t mono">{t.headword}</span>
                <span className="cap">
                  say <b>{t.supersededBy}</b>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── the terms ─────────────────────────────────────────────────────── */}
      {SEED_TERMS.map((t) => (
        <TermCard key={t.headword} term={t} />
      ))}

      <p className="page__foot">
        A community that cannot say which of its own words are current does not own
        its language — it rents it. Resolve any of the above by headword or alias;
        a retired term answers with what to say instead.
      </p>
    </div>
  );
}
